import React, { useState } from 'react';
import { 
  Drawer,
  Typography, 
  Box, 
  IconButton, 
  Button,
  Chip,
  Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { 
  LocationOn as LocationIcon, 
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  CreditCard as CreditCardIcon,
  Report as ReportIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { getConnectorInfo } from './utils/socketTypes';
import { extractConnectorPowerDetailed, getPowerDisplayInfo, debugChargerPowerData, normalizeConnector, extractConnectorPower } from './utils/powerUtils';
import { getChargerAvailability } from './utils/availability';
import ShareLocationModal from './ShareLocationModal';

const ChargerInfoPanel = ({ 
  charger, 
  chargerGroup = null,
  isOpen, 
  onClose, 
  onToggleFavorite, 
  onShare,
  onPayNow,
  onReport,
  isFavorite = false 
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentChargerIndex, setCurrentChargerIndex] = useState(0);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelSurface = isDark ? alpha(theme.palette.background.paper, 0.98) : '#ffffff';
  const panelGradient = isDark
    ? 'linear-gradient(160deg, rgba(9,12,24,0.98) 0%, rgba(4,7,15,0.96) 100%)'
    : 'linear-gradient(180deg, #ffffff 0%, #f8f5ff 45%, #f2f8ff 100%)';
  const mutedBorder = isDark
    ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
    : '1px solid rgba(0, 0, 0, 0.1)';
  const subtleSurface = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : 'rgba(0, 0, 0, 0.02)';
  const accentSurface = isDark
    ? alpha(theme.palette.primary.main, 0.12)
    : '#e3f2fd';
  const accentBorder = isDark
    ? `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
    : '1px solid #bbdefb';
  const brandGradient = 'linear-gradient(120deg, #5C7CFF 0%, #7D65FF 50%, #B64BFF 100%)';
  const connectorHaloShadow = isDark
    ? `0 12px 26px ${alpha(theme.palette.common.black, 0.65)}`
    : `inset 0 1px 2px ${alpha(theme.palette.common.white, 0.8)}`;


  // Reset index when charger changes - MUST be before early return
  React.useEffect(() => {
    setCurrentChargerIndex(0);
  }, [charger, chargerGroup]);

  if (!charger || !isOpen) {
    return null;
  }

  // Prepare chargers array - if chargerGroup exists, use it, otherwise use single charger
  // Aggregate chargers if group provided
  const allChargers = chargerGroup && chargerGroup.length > 0 ? chargerGroup : [charger];
  const currentCharger = allChargers[currentChargerIndex] || charger;

  // Debug logging for charger group

  const handlePreviousCharger = () => {
    setCurrentChargerIndex(prev => 
      prev > 0 ? prev - 1 : allChargers.length - 1
    );
  };

  const handleNextCharger = () => {
    setCurrentChargerIndex(prev => 
      prev < allChargers.length - 1 ? prev + 1 : 0
    );
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  // Shared availability logic
  const availability = getChargerAvailability(currentCharger) || 'unknown';

  const normalizeConnectorStatus = (statusValue) => {
    if (typeof statusValue === 'boolean') {
      return statusValue ? 'available' : 'unavailable';
    }
    const normalized = (statusValue || '').toString().toLowerCase();
    if (!normalized) return 'unknown';
    if (normalized.includes('fault')) return 'faulted';
    if (normalized.includes('unavailable') || normalized.includes('offline') || normalized.includes('down') || normalized.includes('disabled')) {
      return 'unavailable';
    }
    if (normalized.includes('in_use') || normalized.includes('in-use') || normalized.includes('charging') || normalized.includes('occupied')) {
      return 'in_use';
    }
    if (normalized.includes('available') || normalized.includes('online')) return 'available';
    return 'unknown';
  };

  // Enhanced connector list with individual entries
  const getConnectorEntries = (chargerData) => {
    const connectors = [];
    
    
    // Debug charger data structure for power extraction
    debugChargerPowerData(chargerData);
    
    // Build raw candidate list (webapp stations first, then CMS connectors)
    // Collect stations across all devices & webapp entries
    const webappEntries = Array.isArray(chargerData?.webappData) ? chargerData.webappData : [];
    const stations = webappEntries.flatMap(w => (Array.isArray(w.devices) ? w.devices : [])
      .flatMap(d => (Array.isArray(d.stations) ? d.stations : []))) || [];
  const cmsConnectors = Array.isArray(chargerData?.connectors) ? chargerData.connectors : [];
    const webappConnectorsArray = chargerData?.webappData?.connectors || [];
    let rawList = [];
    if (stations.length) rawList = rawList.concat(stations);
    if (cmsConnectors.length) rawList = rawList.concat(cmsConnectors);
    if (!stations.length && !cmsConnectors.length && webappConnectorsArray.length) rawList = rawList.concat(webappConnectorsArray);
  const connectorsToProcess = rawList.map((r,i) => normalizeConnector(r,i));
    const seen = new Set();
    
    if (connectorsToProcess.length > 0) {
      connectorsToProcess.forEach((nc, index) => {
        const canonicalType = nc.baseType; // already canonical from normalizeConnector
        const connectorInfo = getConnectorInfo(canonicalType || 'Unknown');
        const powerDisplay = extractConnectorPower(nc.raw, chargerData);
        const parsedValue = powerDisplay && powerDisplay.includes('kW')
          ? powerDisplay.split(' ')[0]
          : powerDisplay;
        const powerInfo = getPowerDisplayInfo(parsedValue);

        let statusInput = nc.raw?.status ?? nc.raw?.availability ?? '';
        const hasTextStatus = typeof statusInput === 'string' && statusInput.trim().length > 0;
        const booleanStatus = typeof nc.raw?.isAvailable === 'boolean' ? nc.raw.isAvailable : null;

        if (!hasTextStatus && booleanStatus !== null) {
          statusInput = booleanStatus ? 'available' : 'unavailable';
        } else if (hasTextStatus && booleanStatus !== null) {
          const normalizedText = statusInput.toString().toLowerCase();
          if (!normalizedText || normalizedText === 'unknown' || normalizedText === 'na') {
            statusInput = booleanStatus ? 'available' : 'unavailable';
          }
        }

        const normalizedStatus = normalizeConnectorStatus(statusInput);

        const dedupeKey =
          nc.uniqueKey ||
          `${canonicalType || 'connector'}-${nc.raw?.connector_id || nc.raw?.id || ''}-${nc.raw?.name || ''}-${normalizedStatus}`;
        if (dedupeKey && seen.has(dedupeKey)) {
          return;
        }
        if (dedupeKey) {
          seen.add(dedupeKey);
        }

        connectors.push({
          id: nc.uniqueKey || `${canonicalType || 'connector'}-${index}`,
          type: canonicalType || 'Unknown',
          displayName: connectorInfo.displayName || canonicalType || 'Unknown',
          icon: connectorInfo.icon,
          powerDisplay: powerDisplay,
          powerInfo,
          status: normalizedStatus,
          connectorLabel: nc.raw?.name || nc.raw?.connector_id || `Connector ${index + 1}`,
          index: index + 1,
          vendor: chargerData.team_name || chargerData.operator || 'Generic'
        });
      });
    } else {
      // Fallback: Try to extract from other charger fields
      
      // Check if there are any power-related fields directly on the charger
      const fallbackConnector = {
        power: chargerData.power,
        powerRating: chargerData.maxPower,
        kw: chargerData.powerRating || chargerData.kw
      };
      
  const detailedFallback = extractConnectorPowerDetailed(fallbackConnector, chargerData);
  const powerDisplay = detailedFallback.display;
  const powerInfo = getPowerDisplayInfo(powerDisplay);
      
      // Create a generic connector entry with proper connector info
      const fallbackConnectorInfo = getConnectorInfo('Generic');
      connectors.push({
        id: 'fallback-connector',
        type: 'Generic',
        displayName: fallbackConnectorInfo.displayName,
        icon: fallbackConnectorInfo.icon,
        powerDisplay: powerDisplay,
        powerInfo: powerInfo,
        status: normalizeConnectorStatus(chargerData.status || 'unknown'),
        connectorLabel: chargerData.name || 'Connector',
        index: 1,
        vendor: chargerData.team_name || chargerData.operator || 'Generic'
      });
    }
    
    return connectors;
  };

  // availability already computed above
  const connectorDetails = getConnectorEntries(currentCharger);

  // Group availability summary for header chips (available / in use / unavailable counts)
  const groupAvailabilitySummary = (() => {
    if (allChargers.length <= 1) return null;
    const counts = { available: 0, in_use: 0, unavailable: 0, unknown: 0 };
    allChargers.forEach(ch => {
      const st = getChargerAvailability(ch) || 'unknown';
      if (counts[st] !== undefined) counts[st] += 1; else counts.unknown += 1;
    });
    return counts;
  })();
  const connectorStatusSummary = connectorDetails.reduce((acc, conn) => {
    const key = conn.status || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const stationCount = connectorDetails.length || currentCharger.connectors?.length || 1;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400, md: 450, },
          backgroundColor: panelSurface,
          backgroundImage: panelGradient,
          color: theme.palette.text.primary,
          boxShadow: isDark ? '-6px 0 35px rgba(0,0,0,0.65)' : '-4px 0 20px rgba(0, 0, 0, 0.15)',
          borderTopLeftRadius: 24, // <-- Add this for rounded top left
          borderBottomLeftRadius: 24, // <-- Add this for rounded bottom left
          borderRight: 'none'
        }
      }}
    >
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflowY: 'auto',
        p: 2,
        backgroundColor: isDark ? alpha(theme.palette.common.white, 0.01) : 'transparent'
      }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <LocationIcon sx={{ color: theme.palette.warning.main, mr: 1, fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, fontSize: '1rem' }}>
                {currentCharger.chargepoint_name || currentCharger.name || 'Charging Station'}
              </Typography>
              {groupAvailabilitySummary && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {groupAvailabilitySummary.available > 0 && (
                    <Chip size="small" label={`Avail: ${groupAvailabilitySummary.available}`} color="success" />
                  )}
                  {groupAvailabilitySummary.in_use > 0 && (
                    <Chip size="small" label={`In Use: ${groupAvailabilitySummary.in_use}`} color="warning" />
                  )}
                  {groupAvailabilitySummary.unavailable > 0 && (
                    <Chip size="small" label={`Down: ${groupAvailabilitySummary.unavailable}`} color="error" />
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton size="small" onClick={() => onToggleFavorite && onToggleFavorite(currentCharger, !isFavorite)}>
                  {isFavorite ? (
                    <FavoriteIcon sx={{ color: 'red', fontSize: 20 }} />
                  ) : (
                    <FavoriteBorderIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                  )}
                </IconButton>
                <IconButton size="small" onClick={handleShareClick}>
                  <ShareIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton size="small">
                  <LocationIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Navigation for Multiple Chargers - Enhanced */}
            {(allChargers.length > 1 || (chargerGroup && chargerGroup.length > 1)) && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2,
                p: 1.5,
                backgroundColor: accentSurface,
                borderRadius: 2,
                border: accentBorder
              }}>
                <IconButton 
                  size="small" 
                  onClick={handlePreviousCharger}
                  disabled={allChargers.length <= 1}
                  sx={{
                    backgroundColor: isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.common.white,
                    '&:hover': { backgroundColor: isDark ? alpha(theme.palette.common.white, 0.12) : '#f5f5f5' },
                    '&:disabled': { backgroundColor: isDark ? alpha(theme.palette.common.white, 0.04) : '#f5f5f5' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="primary" sx={{ 
                    fontSize: '0.8rem', 
                    fontWeight: 'bold',
                    display: 'block'
                  }}>
                    Multiple Chargers Available
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {currentChargerIndex + 1} of {allChargers.length} Chargers
                  </Typography>
                </Box>
                
                <IconButton 
                  size="small" 
                  onClick={handleNextCharger}
                  disabled={allChargers.length <= 1}
                  sx={{
                    backgroundColor: isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.common.white,
                    '&:hover': { backgroundColor: isDark ? alpha(theme.palette.common.white, 0.12) : '#f5f5f5' },
                    '&:disabled': { backgroundColor: isDark ? alpha(theme.palette.common.white, 0.04) : '#f5f5f5' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            )}

            {/* Address */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
              {currentCharger.street || currentCharger.address || 'Address not available'}
            </Typography>

            {/* Charge Points Summary */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ mr: 1, fontSize: '0.85rem' }}>
                {stationCount} Charge Point{stationCount > 1 ? 's' : ''}
              </Typography>
              <Chip
                label={availability === 'available' ? 'Available' : 
                       availability === 'in_use' ? 'In Use' : 
                       availability === 'unavailable' ? 'Unavailable' : 'Unknown'}
                size="small"
                sx={{
                  backgroundColor: availability === 'available'
                    ? alpha(theme.palette.success.main, 0.2)
                    : availability === 'in_use'
                      ? alpha(theme.palette.warning.main, 0.2)
                      : availability === 'unavailable'
                        ? alpha(theme.palette.error.main, 0.2)
                        : alpha(theme.palette.grey[500], 0.2),
                  color: availability === 'available'
                    ? theme.palette.success.main
                    : availability === 'in_use'
                      ? theme.palette.warning.main
                      : availability === 'unavailable'
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  fontSize: '0.7rem',
                  height: 20,
                  border: availability === 'unknown' ? mutedBorder : 'none'
                }}
              />
            </Box>

            {/* Info Link */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Info
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Station Name (Repeat) */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, fontSize: '1rem' }}>
              {currentCharger.chargepoint_name || currentCharger.name || 'Charging Station'}
            </Typography>

            {/* Connector Status Summary */}
            {!!connectorDetails.length && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {connectorStatusSummary.available && (
                  <Chip size="small" label={`Available: ${connectorStatusSummary.available}`} color="success" />
                )}
                {connectorStatusSummary.in_use && (
                  <Chip size="small" label={`In Use: ${connectorStatusSummary.in_use}`} color="warning" />
                )}
                {connectorStatusSummary.unavailable && (
                  <Chip size="small" label={`Unavailable: ${connectorStatusSummary.unavailable}`} color="error" />
                )}
                {connectorStatusSummary.faulted && (
                  <Chip size="small" label={`Faulted: ${connectorStatusSummary.faulted}`} color="error" variant="outlined" />
                )}
              </Box>
            )}

            {/* Connector Details */}
            {connectorDetails.map((connector, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  mb: 1,
                  backgroundColor: subtleSurface,
                  borderRadius: 2,
                  border: mutedBorder
                }}
              >
                {/* Connector Icon with Count Badge */}
                <Box sx={{ position: 'relative', mr: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: isDark
                        ? alpha(theme.palette.common.white, 0.08)
                        : '#f5f5f5',
                      ...(isDark
                        ? {
                            backgroundImage: brandGradient,
                            border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                            boxShadow: connectorHaloShadow
                          }
                        : {
                            border: '2px solid #e0e0e0',
                            boxShadow: connectorHaloShadow
                          }),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="img"
                      src={connector.icon}
                      alt={connector.type}
                      sx={{
                        width: 20,
                        height: 20,
                        filter: isDark ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.65)) brightness(1.1)' : 'none'
                      }}
                      onError={(e) => {
                        e.target.src = '/assets/connectorSvg/img_connector_1.svg';
                      }}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.9rem' }}>
                    {connector.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {connector.connectorLabel} • #{connector.index}
                  </Typography>
                  
                  {/* Enhanced power display with color coding and speed label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.85rem', 
                        fontWeight: '700',
                        color: connector.powerInfo?.color || 'primary.main'
                      }}
                    >
                      {connector.powerDisplay !== 'N/A' ? connector.powerDisplay : 'Power N/A'}
                    </Typography>
                    
                    {connector.powerInfo?.speedLabel && connector.powerDisplay !== 'N/A' && (
                      <Chip 
                        label={connector.powerInfo.speedLabel} 
                        size="small" 
                        sx={{ 
                          height: 18, 
                          fontSize: '0.65rem', 
                          fontWeight: 600,
                          bgcolor: alpha(connector.powerInfo.color || theme.palette.primary.main, 0.15),
                          color: connector.powerInfo.color || theme.palette.primary.main,
                          border: `1px solid ${alpha(connector.powerInfo.color || theme.palette.primary.main, 0.3)}`
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                    {connector.vendor || currentCharger.team_name || currentCharger.operator || 'Generic'}
                  </Typography>
                </Box>
                
                <Chip
                  label={connector.status === 'available' ? 'Available' : 
                         connector.status === 'in_use' ? 'In Use' : 
                         connector.status === 'faulted' ? 'Faulted' : 
                         connector.status === 'unavailable' ? 'Unavailable' : 'Unknown'}
                  color={connector.status === 'available' ? 'success' : 
                         connector.status === 'in_use' ? 'warning' : 
                         connector.status === 'faulted' || connector.status === 'unavailable' ? 'error' : 'default'}
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Box>
            ))}

            {/* Action Buttons */}
            {/* <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<CreditCardIcon />}
                onClick={onPayNow}
                sx={{
                  flex: 1,
                  backgroundColor: theme.palette.success.main,
                  '&:hover': { backgroundColor: theme.palette.success.dark },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                PAY NOW
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReportIcon />}
                onClick={onReport}
                sx={{
                  flex: 1,
                  borderColor: theme.palette.warning.main,
                  color: theme.palette.warning.main,
                  '&:hover': { 
                    borderColor: theme.palette.warning.dark, 
                    backgroundColor: alpha(theme.palette.warning.main, 0.08) 
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                ▲ REPORT
              </Button>
            </Box> */}
      </Box>

      {/* Share Location Modal */}
      <ShareLocationModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        charger={charger}
      />
    </Drawer>
  );
};

export default ChargerInfoPanel;